/**
 * NoteEditor - Generic note editing component with its own Refrakt store
 *
 * Props:
 * - slug: Slug of note to edit (loads or creates draft when changed)
 * - projectId: Current project ID
 * - accessToken: Auth token for API calls
 *
 * Events:
 * - save-success: Save completed (detail: Slug)
 * - save-error: Save failed (detail: string)
 */
import {
  css,
  html,
  LitElement,
  type PropertyValues,
  type TemplateResult,
} from "lit";
import { SignalWatcher } from "@lit-labs/signals";
import { customElement, property } from "lit/decorators.js";
import * as Styles from "../../styles.ts";
import { toTag } from "../../../util/tag.ts";
import {
  type Action,
  createNoteEditStore,
  type Model,
  model,
  type NoteEditStore,
} from "./store.ts";
import "../dui/vstack/dui-vstack.ts";
import "../dui/hstack/dui-hstack.ts";
import "../dui/field/dui-field.ts";
import "../dui/field/dui-field-label.ts";
import "../dui/field/dui-field-description.ts";
import "../dui/input/dui-input.ts";
import "../dui/textarea/dui-textarea.ts";
import "../app/loading.ts";

const style = css`
  :host {
    display: block;
  }

  .title-textfield {
    --textfield-font-size: var(--lg-size);
    --textfield-line-height: var(--lg-line);
  }

  .content-textfield {
    min-height: calc(var(--u) * 40);
  }

  .meta-textfield {
    font-family: "Fira Mono", monospace;
    font-size: var(--sm-size);
    min-height: calc(var(--u) * 20);
  }

  .meta-error {
    color: var(--red);
    font-size: var(--sm-size);
    margin-top: var(--gap-sm);
  }
`;

/** Parse space-separated tag input into sanitized hashtags */
const parseTags = (input: string): string[] =>
  input
    .split(/\s+/)
    .map((text) => toTag(text.trim()))
    .filter((tag): tag is string => tag !== undefined);

const NoteEditFormView = (
  state: Model,
  send: (action: Action) => void,
): TemplateResult => {
  const onTitleInput = (event: CustomEvent<{ value: string }>) => {
    send({
      type: "set-title",
      value: event.detail.value,
      accessToken: state.accessToken,
    });
  };

  const onContentInput = (event: CustomEvent<{ value: string }>) => {
    send({
      type: "set-content",
      value: event.detail.value,
      accessToken: state.accessToken,
    });
  };

  const onTypeInput = (event: CustomEvent<{ value: string }>) => {
    send({
      type: "set-type",
      value: event.detail.value,
      accessToken: state.accessToken,
    });
  };

  const onTagsInput = (event: InputEvent) => {
    const value = (event.target as HTMLInputElement).value;
    const tags = parseTags(value);
    send({ type: "set-tags", value: tags, accessToken: state.accessToken });
  };

  const onMetaJsonInput = (event: CustomEvent<{ value: string }>) => {
    send({
      type: "set-meta-json",
      value: event.detail.value,
      accessToken: state.accessToken,
    });
  };

  return html`
    <dui-vstack>
      <dui-field>
        <dui-field-label>Title</dui-field-label>
        <dui-input
          class="title-textfield"
          @input-change="${onTitleInput}"
          .value="${state.title}"
          placeholder="Untitled"
        ></dui-input>
      </dui-field>

      <dui-field>
        <dui-field-label>Content</dui-field-label>
        <dui-textarea
          class="content-textfield"
          @textarea-change="${onContentInput}"
          .value="${state.content}"
          rows="14"
          placeholder="Note content..."
        ></dui-textarea>
        <dui-field-description
          >The main content of the note (supports
          markdown)</dui-field-description
        >
      </dui-field>

      <dui-field>
        <dui-field-label>Tags</dui-field-label>
        <dui-input
          class="tags-input"
          type="text"
          @input="${onTagsInput}"
          .value="${state.tags.join(" ")}"
          placeholder="tag1, tag2, tag3"
        ></dui-input>
        <dui-field-description>Space-separated tags</dui-field-description>
      </dui-field>

      <dui-collapsible>
        <div slot="title">Metadata</div>
        <dui-vstack slot="content">
          <dui-field>
            <dui-field-label>Type</dui-field-label>
            <dui-input
              @input-change="${onTypeInput}"
              .value="${state.type}"
              placeholder="note"
            ></dui-input>
            <dui-field-description>
              What kind of note is this? Examples: note, hypothesis, plan,
              force...
            </dui-field-description>
          </dui-field>

          <dui-field>
            <dui-field-label>Meta (JSON)</dui-field-label>
            <dui-input
              class="meta-textfield"
              @input-change="${onMetaJsonInput}"
              .value="${state.metaJsonString}"
              placeholder="{}"
            ></dui-input>
            ${state.metaJsonError
              ? html` <div class="meta-error">${state.metaJsonError}</div> `
              : null}
            <dui-field-description>JSON metadata</dui-field-description>
          </dui-field>
        </dui-vstack>
      </dui-collapsible>
    </dui-vstack>
  `;
};

const NoteEditEmptyView = (): TemplateResult => {
  return html` <loading-view size="md"></loading-view> `;
};

@customElement("note-edit-form")
export class NoteEditFormElement extends SignalWatcher(LitElement) {
  static override styles = [Styles.legacyBase, style];

  /** Slug of note to edit */
  @property({ type: String })
  accessor slug: string | undefined = undefined;

  /** Current project ID */
  @property({ type: String })
  accessor projectId: string | undefined = undefined;

  /** Auth token for API calls */
  @property({ type: String })
  accessor accessToken: string | undefined = undefined;

  #store: NoteEditStore = createNoteEditStore(model(), this);

  override willUpdate(changedProperties: PropertyValues<this>): void {
    // When accessToken changes, update the store
    if (changedProperties.has("accessToken")) {
      this.#store.send({
        type: "set-access-token",
        accessToken: this.accessToken,
      });
    }

    // When slug or projectId changes, send load-note action.
    // The store decides whether to actually load (requires both values).
    if (changedProperties.has("slug") || changedProperties.has("projectId")) {
      this.#store.send({
        type: "load-note",
        slug: this.slug,
        projectId: this.projectId,
        accessToken: this.accessToken,
      });
    }
  }

  override render() {
    const state = this.#store.get();

    if (state.slug && state.projectId) {
      return NoteEditFormView(state, this.#store.send);
    } else {
      return NoteEditEmptyView();
    }
  }
}
